import { purchaseOrderLifecycleStage, remainingQuantity } from "../domain/models";
import type { PurchaseOrder, PurchaseOrderCandidate } from "../domain/models";
import type { PurchaseOrderFilter as OrderFilter } from "./purchase-order-workspace-types";

export const lifecycleSteps = [
  { id: "DECISION", label: "Nihai karar" },
  { id: "DRAFT", label: "Sipariş taslağı" },
  { id: "SENT", label: "Tedarikçiye iletim" },
  { id: "DELIVERY", label: "Teslimat" },
  { id: "QUALITY", label: "Kalite" },
  { id: "RESULT", label: "Sonuç" },
] as const;

export const filterLabels: Record<OrderFilter, string> = {
  ACTION: "Sıradaki işler",
  RISK: "Müdahale gereken",
  MOVING: "Teslimat sürecinde",
  RESULT: "Sonuçlanan",
};

export function money(amount: number, currency: string) {
  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(amount);
}

export function number(value: number) {
  return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(
    value,
  );
}

export function fileSize(bytes: number) {
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 1 }).format(bytes / (1024 * 1024))} MB`;
}

export function parsedDate(value: string | null) {
  if (!value) return null;
  const parsed = new Date(`${value.slice(0, 10)}T12:00:00`);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

export function date(value: string | null) {
  const parsed = parsedDate(value);
  if (!parsed) return "Tarih bilgisi yok";
  return new Intl.DateTimeFormat("tr-TR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(parsed);
}

export function percent(value: number | null) {
  return value === null ? "Veri yok" : `%${Math.round(value * 100)}`;
}

export function daysUntil(value: string) {
  const parsed = parsedDate(value);
  if (!parsed) return null;
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  return Math.ceil((parsed.getTime() - today.getTime()) / 86_400_000);
}

export function nextAction(
  order: PurchaseOrder,
  isSpecialist: boolean,
  isQuality: boolean,
) {
  const stage = purchaseOrderLifecycleStage(order);
  if (stage === "COMPLETED") return "Süreç tamamlandı";
  if (stage === "QUALITY_PENDING")
    return isQuality ? "Kalite sonucunu kaydet" : "Kalite Uzmanı inceleyecek";
  if (stage === "QUALITY_REJECTED") {
    const capa = order.deliveries.find((delivery) => delivery.capaNo);
    if (!capa) return "İade veya ikame süreci takip edilecek";
    if (capa.capaStatus === "UNDER_REVIEW")
      return "DÖF değerlendirmesi bekleniyor";
    if (capa.capaStatus === "ACTION_IN_PROGRESS") return "Faaliyet tedarikçide";
    if (capa.capaStatus === "EFFECTIVENESS_REVIEW")
      return "Etkinlik kontrolü bekleniyor";
    return capa.capaStatus === "CLOSED"
      ? "DÖF kapatıldı"
      : "DÖF süreci takip edilecek";
  }
  if (order.status === "CANCELLED") return "İptal kaydı salt okunur";
  if (!isSpecialist) return "Satın Alma Uzmanı takibinde";
  if (order.status === "CREATED") return "Tedarikçiye ilet";
  return "Teslimat kaydet";
}

export function lifecycleStepIndex(order: PurchaseOrder) {
  const stage = purchaseOrderLifecycleStage(order);
  if (stage === "DRAFT") return 1;
  if (stage === "SENT") return 2;
  if (stage === "DELIVERY") return 3;
  if (stage === "QUALITY_PENDING" || stage === "QUALITY_REJECTED") return 4;
  return 5;
}

export function riskOf(order: PurchaseOrder) {
  const stage = purchaseOrderLifecycleStage(order);
  if (["COMPLETED", "CANCELLED"].includes(stage)) return "NONE" as const;
  if (stage === "QUALITY_REJECTED") return "HIGH" as const;
  if (order.overdue || order.supplierPerformance.risk === "HIGH")
    return "HIGH" as const;
  const remainingDays = daysUntil(order.expectedDeliveryAt);
  if (
    (remainingDays !== null && remainingDays <= 2) ||
    order.supplierPerformance.risk === "MEDIUM"
  ) {
    return "MEDIUM" as const;
  }
  return "LOW" as const;
}

export function remainingExplanation(order: PurchaseOrder) {
  const remaining = remainingQuantity(order);
  const replacementPending = Math.min(remaining, order.replacementQuantity);
  const incompletePending = Math.max(0, remaining - replacementPending);
  const parts: string[] = [];
  if (incompletePending > 0)
    parts.push(`${number(incompletePending)} ${order.unit} eksik teslimat`);
  if (replacementPending > 0)
    parts.push(`${number(replacementPending)} ${order.unit} iade ikamesi`);
  if (order.deductedQuantity > 0)
    parts.push(
      `${number(order.deductedQuantity)} ${order.unit} iade siparişten düşüldü`,
    );
  return parts.join(" · ");
}

export function visiblePurchaseOrders(
  orders: PurchaseOrder[], filter: OrderFilter, isQuality: boolean,
) {
  return orders.filter((order) => {
    if (filter === "ACTION")
      return isQuality
        ? order.deliveries.some(
            (delivery) => delivery.qualityStatus === "PENDING",
          )
        : order.status === "CREATED";
    if (filter === "RISK") return riskOf(order) === "HIGH";
    if (filter === "MOVING")
      return !["DRAFT", "COMPLETED", "CANCELLED"].includes(
        purchaseOrderLifecycleStage(order),
      );
    return (
      order.status === "CANCELLED" ||
      purchaseOrderLifecycleStage(order) === "COMPLETED"
    );
  });
}

export function purchaseOrderMetrics(
  orders: PurchaseOrder[], candidates: PurchaseOrderCandidate[],
  isQuality: boolean, isSpecialist: boolean,
) {
  return ({
    action: isQuality
      ? orders.filter((order) =>
          order.deliveries.some(
            (delivery) => delivery.qualityStatus === "PENDING",
          ),
        ).length
      : candidates.length +
        orders.filter((order) => isSpecialist && order.status === "CREATED")
          .length,
    risk: orders.filter((order) => riskOf(order) === "HIGH").length,
    moving: orders.filter(
      (order) =>
        !["DRAFT", "COMPLETED", "CANCELLED"].includes(
          purchaseOrderLifecycleStage(order),
        ),
    ).length,
    result: orders.filter(
      (order) =>
        order.status === "CANCELLED" ||
        purchaseOrderLifecycleStage(order) === "COMPLETED",
    ).length,
  });
}

export function purchaseOrderFocusFilter(order: PurchaseOrder, isQuality: boolean): OrderFilter | null {
  const stage = purchaseOrderLifecycleStage(order);
  if (stage === "COMPLETED" || stage === "CANCELLED") return "RESULT";
  if (isQuality && stage === "DRAFT" && !order.deliveries.some(delivery => delivery.qualityStatus === "PENDING")) return null;
  if (order.status === "CREATED" || isQuality && stage === "QUALITY_PENDING") return "ACTION";
  return "MOVING";
}
